import { useMemo, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import type { GridColDef } from '@mui/x-data-grid';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { civilToday, formatCurrency, isCivilDate, links } from '@eis/core';
import {
  EmptyState,
  InfiniteDataGrid,
  RequirePermission,
  StatTiles,
  cardSurfaceSx,
} from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import { useInfiniteList } from '../api/group-policies';
import {
  isClaimServiceDown,
  toPolicyClaimRow,
  useClaimColumns,
  type PolicyClaimRow,
} from '../../policies/ui/PolicyClaimsTab';

/**
 * The group policy's "Schäden" tab (SDD-018 §9, ui-spec §7; prompt 26 phase 26.7): the members'
 * claims — claimservice books a member's claim on its group policy (`accountCode==<code>`, C-8).
 * The same columns as the policy tab plus the member ("Police") and the insured object; every row
 * says it is "Deckung unter Gruppenvertrag {code}". The header counts the LOADED rows and sums
 * their claimed amounts, and says so — never a total the list did not compute (the Policen tab's
 * Q-3 rule). "Schaden melden" picks the member first: the claim is the member's, never the group's.
 */
export function GroupPolicyClaimsTab({
  code,
  locale,
}: {
  readonly code: string;
  readonly locale: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const navigate = useNavigate();
  const [picking, setPicking] = useState(false);
  const list = useInfiniteList('claims', { filters: `accountCode==${code}`, order: '-damageDate' });
  const rows = useMemo(() => list.items.map(toPolicyClaimRow), [list.items]);
  const base = useClaimColumns(locale);
  const columns = useMemo<GridColDef<PolicyClaimRow>[]>(() => {
    const [number, ...rest] = base;
    const member: GridColDef<PolicyClaimRow> = {
      field: 'policyCode',
      headerName: t('claims.columns.member'),
      width: 130,
      sortable: false,
      renderCell: ({ row }) => (
        <Link
          href={links.insurance.policy(row.policyCode)}
          sx={{ fontFamily: 'monospace' }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void navigate({ href: links.insurance.policy(row.policyCode) });
          }}
        >
          {row.policyCode}
        </Link>
      ),
    };
    const object: GridColDef<PolicyClaimRow> = {
      field: 'objectCode',
      headerName: t('claims.columns.object'),
      width: 150,
      sortable: false,
      valueFormatter: (value: string) => (value === '' ? '—' : value),
    };
    const cover: GridColDef<PolicyClaimRow> = {
      field: 'cover',
      headerName: t('claims.columns.cover'),
      width: 260,
      sortable: false,
      valueGetter: () => t('claims.groupCover', { code }),
    };
    return [...(number === undefined ? [] : [number]), member, object, ...rest, cover];
  }, [base, t, code, navigate]);

  const totalCents = rows.reduce((sum, row) => sum + Math.round((row.amount ?? 0) * 100), 0);

  return (
    <Stack spacing={2.5} data-testid="group-policy-claims">
      {list.items.length > 0 && (
        <StatTiles
          label={t('claims.kpiLabel')}
          items={[
            {
              label: t('claims.loadedCount'),
              value: <span data-testid="claims-count">{rows.length}</span>,
            },
            {
              label: t('claims.loadedTotal'),
              value: (
                <span data-testid="claims-total">{formatCurrency(totalCents, 'EUR', locale)}</span>
              ),
              tone: 'accent',
            },
          ]}
        />
      )}
      <Paper variant="outlined" sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" component="h2">
                {t('claims.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('claims.groupDescription', { code })}
              </Typography>
            </Box>
            <RequirePermission anyOf={[p.claims.create]}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setPicking(true);
                }}
              >
                {t('claims.report')}
              </Button>
            </RequirePermission>
          </Stack>
          <InfiniteDataGrid
            rows={rows}
            list={list}
            columns={columns}
            ariaLabel={t('claims.ariaLabel')}
            errorDescription={
              isClaimServiceDown(list.error) ? t('claims.unavailable') : t('claims.error')
            }
            emptyTitle={t('claims.empty.title')}
            emptyDescription={t('claims.empty.groupDescription')}
            onRowClick={(row) => {
              void navigate({ href: links.claims.claim(row.code) });
            }}
          />
        </Stack>
      </Paper>
      {picking && (
        <MemberPicker
          code={code}
          onClose={() => {
            setPicking(false);
          }}
          onPick={(policyCode, damageDate) => {
            void navigate({ href: links.claims.newClaim({ policyCode, damageDate }) });
          }}
        />
      )}
    </Stack>
  );
}

/**
 * "Schaden melden" for a group: the member first, found by plate or name among the members in
 * force on the damage date (the member list's own search and `activeOn`, SDD-017 §9).
 */
function MemberPicker({
  code,
  onClose,
  onPick,
}: {
  readonly code: string;
  readonly onClose: () => void;
  readonly onPick: (policyCode: string, damageDate: string) => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const [search, setSearch] = useState('');
  const [damageDate, setDamageDate] = useState('');
  const validAt = isCivilDate(damageDate) ? damageDate : undefined;
  const members = useInfiniteList(
    'groupPolicyMembers',
    {
      ...(search.trim() === '' ? {} : { search: search.trim() }),
      ...(validAt === undefined ? {} : { validAt }),
      pageSize: 20,
    },
    { code },
  );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="claim-member-picker">
      <DialogTitle id="claim-member-picker">{t('claims.picker.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('claims.picker.description', { code })}
          </Typography>
          <TextField
            size="small"
            type="date"
            label={t('claims.picker.damageDate')}
            value={damageDate}
            onChange={(event) => {
              setDamageDate(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: civilToday() } }}
          />
          <TextField
            size="small"
            label={t('claims.picker.search')}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
          {members.isLoading ? (
            <Skeleton variant="rounded" height={120} />
          ) : members.items.length === 0 ? (
            <EmptyState description={t('claims.picker.none')} />
          ) : (
            <List dense aria-label={t('claims.picker.members')}>
              {members.items.map((member) => (
                <ListItem key={member.policyCode} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      onPick(member.policyCode ?? '', validAt ?? '');
                    }}
                  >
                    <ListItemText
                      primary={member.policyCode}
                      secondary={
                        member.insuredPartnerName === undefined || member.insuredPartnerName === ''
                          ? member.insuredPartnerId
                          : member.insuredPartnerName
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('claims.picker.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
}
